import Text from '@deprecated-ui/common/Text';
import Flex from '@deprecated-ui/common/Flex';
import Modal from '@deprecated-ui/common/Modal';
import Iconmoon from '@deprecated-ui/common/Iconmoon';
import color from '@deprecated-ui/_style/color';
import { useEffect, useMemo, useRef, useState } from 'react';

import If from 'global/ErrorHandlers/If';
import {
  trackAutoOrderCanceled,
  trackAutoSellAlterOrderSelected,
  trackAutoSellAutoOrderView,
  trackAutoSellDeleteCanceled,
  trackAutoSellOrderDeleted,
  trackAutoSellOrderTypeSelected,
} from 'lib/analytics/autoOrder';
import tradingModalStore from 'global/TradingModal/store';
import { withMaintenanceGuard } from 'global/Securities/utils/maintenance';

import {
  orderTypeOptions,
  smartOrderText,
} from 'global/Securities/constants/transactionForm';
import {
  SellOrderType,
  SmartOrderCode,
  SmartOrderType,
} from 'global/Securities/types/transaction';
import usePortfolioDetailStore from 'global/Securities/stores/usePortfolioDetailStore';
import shallow from 'zustand/shallow';
import useTradingStore from 'features/transaction/useTradingStore';
import useOrderDetailStore from 'global/Securities/stores/useOrderDetailStore';
import tradingOperationStore from 'lib/stores/tradingOperation';
import useTradingAccount from 'hooks/useTradingAccount';
import { ModalButtonContainer, ModalTradingButton } from '../styled';
import FormModal from './FormModal';
import { Option } from './styled';

import store from './store';
import { iModal } from '../types';

const MODAL_TYPES = {
  menu: 'menu',
  info: 'info',
  form: 'form',
  delete: 'delete',
};

interface OptionItem {
  type: SellOrderType;
  text: string;
}

interface iAutoOrder extends iModal {
  symbol: string;
  initialType?: SellOrderType;
}

const options: OptionItem[] = [
  { type: SellOrderType.StopLoss, text: orderTypeOptions.Sell.StopLoss },
  { type: SellOrderType.TakeProfit, text: orderTypeOptions.Sell.TakeProfit },
];

const orderLabelMapper = {
  [SmartOrderCode.StopLoss]: SmartOrderType.StopLoss,
  [SmartOrderCode.TakeProfit]: SmartOrderType.TakeProfit,
};

export const AutoOrder = ({
  visible,
  onCancel,
  symbol,
  initialType,
}: iAutoOrder) => {
  const isCheckInitialAmend = useRef(false);

  const resetOrderDetail = useOrderDetailStore((s) => s.resetOrderDetail);
  const autoOrderId = useTradingStore((s) => s.autoOrderId);
  const orderLabel = useTradingStore((s) => s.orderLabel);
  const orderGtc = useTradingStore((s) => s.orderGtc);
  const smartOrderId = useTradingStore((s) => s.smartOrderId);

  const { portfolio, loadPortfolioDetail } = usePortfolioDetailStore(
    (s) => ({
      portfolio: s.data,
      loadPortfolioDetail: s.loadPortfolioDetail,
    }),
    shallow,
  );

  const { isRealTrading } = useTradingAccount();

  const { postTradeCancel } = tradingOperationStore(
    (s) => ({
      postTradeCancel: s.postTradeCancel,
    }),
    shallow,
  );

  const { have_smartorder } = portfolio;
  const initialModal = MODAL_TYPES.menu;
  const [openModal, setOpenModal] = useState(initialModal);
  const [currentType, setCurrentType] = useState<SellOrderType>(initialType);
  const [prevModal, setPrevModal] = useState('');
  const {
    stockData,
    deleteSmartOrder,
    getStockData,
    resetStockData,
    isLoading,
  } = store();
  const setAutoBuy = tradingModalStore((s) => s.setAutoBuy);

  const autoOrderData = useMemo(
    () => stockData.find((item) => item.id === autoOrderId),
    [stockData, autoOrderId],
  );

  useEffect(() => {
    resetStockData();
  }, []);

  useEffect(() => {
    if (have_smartorder) {
      getStockData({ stock_code: symbol });
    }
  }, [symbol, have_smartorder]);

  useEffect(() => {
    if (initialModal === MODAL_TYPES.menu) {
      trackAutoSellAutoOrderView();
    }
  }, [initialModal]);

  useEffect(() => {
    if (autoOrderData?.type_text) {
      const newType = options.find(
        (opt) => opt.text === autoOrderData.type_text,
      );
      if (newType) {
        setCurrentType(newType.type);
      }
    }
  }, [autoOrderData]);

  const resetData = async () => {
    await resetStockData();
    isCheckInitialAmend.current = false;

    resetOrderDetail();
    setOpenModal(initialModal);
    setPrevModal('info');
    setAutoBuy(false);
    onCancel();
  };

  const deleteAutoOrder = async () => {
    if (autoOrderData) {
      await deleteSmartOrder(
        { id: autoOrderData?.id },
        autoOrderData?.type_text,
      );
      resetData();
    }

    if (!autoOrderData && autoOrderId && !smartOrderId) {
      await postTradeCancel({
        orderkey: `W-WITHDRAW-${Date.now()}`,
        orderid: autoOrderId,
        gtc: orderGtc,
        symbol,
        isRealTrading,
      });
      resetData();
    }
  };

  const handleOptionClick = (opt: OptionItem) => () => {
    withMaintenanceGuard(() => {
      trackAutoSellOrderTypeSelected(opt.text, symbol);
      setOpenModal(MODAL_TYPES.form);
      setPrevModal(MODAL_TYPES.menu);
      setCurrentType(opt.type);
    });
  };

  useEffect(() => {
    loadPortfolioDetail(symbol);
  }, [symbol]);

  useEffect(() => {
    if (autoOrderId && !isCheckInitialAmend.current) {
      setOpenModal(MODAL_TYPES.form);
    }

    isCheckInitialAmend.current = true;
  }, [autoOrderId]);

  useEffect(() => {
    if (initialType) {
      setCurrentType(initialType);
      setOpenModal(MODAL_TYPES.form);
    } else {
      setCurrentType(SellOrderType.StopLoss);
    }
  }, [initialType]);

  useEffect(() => {
    // AMEND ONLY
    if (!autoOrderData && autoOrderId && !smartOrderId && orderLabel) {
      setCurrentType(orderLabelMapper[orderLabel]);
    }
  }, [autoOrderData, autoOrderId, orderLabel, smartOrderId]);

  if (!visible) return null;

  return (
    <>
      <If condition={openModal === 'menu'}>
        <Modal
          visible={visible}
          onCancel={() => {
            if (prevModal) setOpenModal(prevModal);
            else {
              onCancel();
              trackAutoOrderCanceled();
            }
          }}
          centered
          width={520}
        >
          {/* @ts-expect-error */}
          <Text align="center" family="bold" size="18px" p="16px">
            Auto Order {symbol}
          </Text>
          {options.map((opt) => (
            <Option key={opt.type} onClick={handleOptionClick(opt)}>
              <div>
                <Text size="16px" color={color.highempahis}>
                  {opt.text}
                </Text>
              </div>
              <Iconmoon
                className="icon-trading_back reverse"
                color={color.highempahis}
              />
            </Option>
          ))}
        </Modal>
      </If>
      <If condition={openModal === MODAL_TYPES.delete}>
        <Modal
          visible={openModal === MODAL_TYPES.delete}
          onCancel={() => {
            if (prevModal) setOpenModal(prevModal);
            else onCancel();
            trackAutoSellOrderDeleted();
          }}
          centered
          width={520}
        >
          <Flex
            direction="column"
            align="center"
            justify="space-around"
            // @ts-expect-error
            p="60px 40px"
          >
            {/* @ts-expect-error */}
            <Text align="center" family="bold" size="18px" mb="16px">
              Hapus {orderTypeOptions.Sell[currentType]}
            </Text>
            <Text color={color.highgray}>
              Kamu ingin menghapus {smartOrderText[currentType]} ini?
            </Text>
          </Flex>
          <ModalButtonContainer>
            <ModalTradingButton
              style={{
                backgroundColor: color.light,
              }}
              onClick={() => {
                if (prevModal) {
                  setOpenModal(prevModal);
                } else onCancel();
                trackAutoSellDeleteCanceled();
              }}
            >
              <Text color={color.primary} family="bold" size="16px">
                Cancel
              </Text>
            </ModalTradingButton>
            <ModalTradingButton
              className="sell"
              onClick={deleteAutoOrder}
              loading={isLoading}
            >
              Delete
            </ModalTradingButton>
          </ModalButtonContainer>
        </Modal>
      </If>
      <If condition={openModal === MODAL_TYPES.form}>
        <FormModal
          visible={openModal === 'form'}
          onCancel={onCancel}
          type={SmartOrderCode[currentType]}
          portfolio={portfolio}
          afterSubmit={resetData}
          onDelete={() => {
            setPrevModal(MODAL_TYPES.form);
            setOpenModal(MODAL_TYPES.delete);
            trackAutoSellAlterOrderSelected('Add');
          }}
        />
      </If>
    </>
  );
};

export default AutoOrder;
